<script setup lang="ts">
import { computed } from 'vue'

import QuantityPicker from './QuantityPicker.vue'

interface Props {
  name: string
  description?: string
  min: number
  max: number
}

const props = defineProps<Props>()

const quantity = defineModel<number>({ required: true })

const showMin = computed(() => props.min > 0)
const showMax = computed(() => props.max > 0 && quantity.value >= props.max)
</script>

<template>
  <div class="variant-row">
    <div class="info">
      <h4 class="name">{{ name }}</h4>
      <div v-if="description" class="description">{{ description }}</div>
    </div>
    <div v-if="showMin || showMax" class="limits">
      <div v-if="showMin" class="limit">Min: {{ min }}</div>
      <div v-if="showMax" class="limit">Max: {{ max }}</div>
    </div>
    <div class="quantity-picker-container">
      <QuantityPicker v-model="quantity" :max="max || Infinity" :min="min" />
    </div>
  </div>
</template>

<style scoped>
.variant-row {
  border-style: solid;
  border-color: var(--action-card-border);
  border-width: var(--border-width);
  border-radius: var(--radius-medium);
  background-color: var(--action-card-bg);
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, auto);
  gap: var(--space-0-5);
  padding: var(--space-0-75);
  justify-content: space-between;
  align-items: start;
  margin-bottom: var(--space-0-75);

  @media (--desktop-experience) {
    display: flex;
    flex-direction: row;
    padding: var(--space-1);
    align-items: center;
    gap: var(--space-1);
  }

  .info {
    font-size: var(--size-sm);
    grid-row: 1 / 3;
    align-self: center;

    @media (--desktop-experience) {
      flex: 0 0 40%;
    }
  }

  .limits {
    display: flex;
    gap: var(--space-0-25);
    justify-content: end;

    @media (--desktop-experience) {
      justify-content: flex-start;
      margin-left: auto;
    }
  }

  .quantity-picker-container {
    grid-column: 2;
    grid-row: 2;
    text-align: right;

    @media (--desktop-experience) {
      grid-column: unset;
      grid-row: unset;
      text-align: unset;
    }
  }

  .limit {
    background: var(--elevation-0);
    padding: var(--space-0-25) var(--space-0-5);
    border-radius: var(--radius-small);
    border: var(--border-width) solid var(--action-card-border);
    white-space: nowrap;
    font-size: var(--size-xs);
    align-self: center;
  }
}
</style>
